// ============================================================================
// OINP employer eligibility checker — 9 questions.
// Questions 3 and 4 are not static: their thresholds are rewritten from the
// answer to question 1, because the revenue floor and the required headcount
// are both keyed to the work location.
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  Wrapper, Progress, Eyebrow, StepTitle, StepSubtitle,
  OptionGrid, OptionCard, NavRow, BackBtn, NextBtn,
} from '../assessments/oinp/ui';
import { translator } from '../assessments/oinp/i18n';
import { employerQuestions, evaluateEmployer } from '../assessments/oinp/engine';

const EMPTY = {};

const OINPEmployerChecker = ({
  onComplete,
  onBack,
  noc = null,
  darkMode = false,
  scheme = 'blue',
  language = 'en',
}) => {
  const tr = translator(language);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState(EMPTY);

  // Re-resolved on every location change so the dynamic prompts stay in sync.
  const questions = useMemo(() => employerQuestions(answers.location), [answers.location]);
  const question = questions[Math.min(index, questions.length - 1)];
  const total = questions.length;
  const isLast = index >= total - 1;
  const answered = answers[question.id] !== undefined;

  const pick = optionId => {
    setAnswers(prev => {
      // Changing the work location invalidates the two threshold answers that
      // were given against the old tier — clear them rather than silently
      // scoring a $250K "yes" against the $1M GTA floor.
      if (question.id === 'location' && prev.location && prev.location !== optionId) {
        const { revenue, employees, ...rest } = prev;
        return { ...rest, location: optionId };
      }
      return { ...prev, [question.id]: optionId };
    });
  };

  const goNext = () => {
    if (isLast) {
      onComplete(evaluateEmployer({ ...answers, noc }), { ...answers, noc });
      return;
    }
    setIndex(i => i + 1);
  };

  const goBack = () => {
    if (index === 0) { onBack && onBack(); return; }
    setIndex(i => i - 1);
  };

  const startOver = () => { setAnswers(EMPTY); setIndex(0); };

  const pct = Math.round(((index + 1) / total) * 100);

  return (
    <Wrapper>
      <Progress
        label={tr('questionOf', { current: index + 1, total })}
        pct={pct}
        darkMode={darkMode}
        scheme={scheme}
        onStartOver={index > 0 ? startOver : null}
        startOverLabel={tr('startOver')}
      />

      <Eyebrow $darkMode={darkMode} $scheme={scheme}>{tr('employerTitle')}</Eyebrow>
      <StepTitle $darkMode={darkMode}>{tr(question.promptKey, question.params)}</StepTitle>
      {question.helpKey && <StepSubtitle $darkMode={darkMode}>{tr(question.helpKey)}</StepSubtitle>}

      <OptionGrid>
        {question.options.map(option => (
          <OptionCard
            key={option.id}
            title={tr(option.labelKey)}
            detail={option.detailKey ? tr(option.detailKey) : null}
            selected={answers[question.id] === option.id}
            onClick={() => pick(option.id)}
            darkMode={darkMode}
            scheme={scheme}
          />
        ))}
      </OptionGrid>

      <NavRow>
        <BackBtn type="button" $darkMode={darkMode} onClick={goBack}>
          {index === 0 ? tr('cancel') : tr('back')}
        </BackBtn>
        <NextBtn type="button" $darkMode={darkMode} $scheme={scheme} disabled={!answered} onClick={goNext}>
          {isLast ? tr('seeResults') : tr('next')}
        </NextBtn>
      </NavRow>
    </Wrapper>
  );
};

export default OINPEmployerChecker;
